<template>
  <div class="slide slide-title-cover">
    <!-- Background Gradient Orbs -->
    <div class="orb orb-1"></div>
    <div class="orb orb-2"></div>
    <div class="orb orb-3"></div>

    <!-- Content -->
    <div class="slide-content">
      <!-- Header -->
      <div class="slide-header">
        <div class="company-badge">
          <span class="label">{{ $t('reports.quarterly.title_cover.badge') }}</span>
        </div>
      </div>

      <!-- Title -->
      <div class="title-section">
        <h1 class="title">{{ $t('reports.quarterly.title_cover.title') }}</h1>
        <div class="subtitle-line"></div>
        <p class="subtitle">{{ data.quarterName }} | {{ data.teamName }}</p>
      </div>

      <!-- Hero Metric -->
      <div class="hero-metric-container">
        <div class="hero-metric" :class="heroMetricClass">
          {{ formatMetricValue(data.heroMetric) }}
        </div>
        <div class="hero-label">{{ data.heroLabel }}</div>
      </div>

      <!-- Stats Row -->
      <div class="stats-row">
        <div
          v-for="(stat, index) in data.stats"
          :key="index"
          class="stat-box"
        >
          <div class="stat-value" :class="getStatClass(stat)">
            {{ formatStatValue(stat) }}
          </div>
          <div class="stat-label">{{ stat.label }}</div>
        </div>
      </div>

      <!-- Footer -->
      <div class="slide-footer">
        <span class="company-name">{{ data.companyName }}</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TitleCoverSlide',
  props: {
    data: {
      type: Object,
      required: true
    },
    slideId: {
      type: String,
      default: ''
    }
  },
  computed: {
    heroMetricClass() {
      const value = this.data.heroMetric;
      if (value >= 30) return 'score-high';
      if (value >= 0) return 'score-medium';
      return 'score-low';
    }
  },
  methods: {
    formatMetricValue(value) {
      if (typeof value !== 'number') return value;
      return value > 0 ? `+${value}` : value;
    },
    formatStatValue(stat) {
      if (stat.isChange) {
        const val = stat.value;
        const prefix = val > 0 ? '+' : '';
        return `${prefix}${val}${stat.suffix || ''}`;
      }
      return `${stat.value}${stat.suffix || ''}`;
    },
    getStatClass(stat) {
      if (!stat.isChange) return {};
      return {
        'is-change-positive': stat.value > 0,
        'is-change-negative': stat.value < 0,
        'is-change-neutral': stat.value === 0
      };
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../../../assets/quarterly-slides.scss';

.slide-title-cover {
  @extend .slide-base;
  background: $ink;
  color: #fff;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 80px;
}

// Gradient Orbs
.orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(100px);
  opacity: 0.4;
}

.orb-1 {
  width: 600px;
  height: 600px;
  background: $violet-500;
  top: -200px;
  right: -100px;
}

.orb-2 {
  width: 400px;
  height: 400px;
  background: $violet-700;
  bottom: -100px;
  left: -100px;
}

.orb-3 {
  width: 300px;
  height: 300px;
  background: $emerald;
  bottom: 200px;
  right: 200px;
  opacity: 0.2;
}

.slide-content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 1200px;
}

.slide-header {
  margin-bottom: 40px;
}

.company-badge {
  .label {
    @extend .mono-label;
    color: $violet-300;
    font-size: 16px;
    letter-spacing: 0.2em;
  }
}

.title-section {
  text-align: center;
  margin-bottom: 60px;
}

.title {
  @extend .serif-headline;
  font-size: 96px;
  margin: 0 0 24px;
  background: linear-gradient(180deg, #fff 0%, $violet-200 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.subtitle-line {
  width: 120px;
  height: 2px;
  background: linear-gradient(90deg, transparent, $violet-400, transparent);
  margin: 0 auto 24px;
}

.subtitle {
  font-family: $font-body;
  font-size: 24px;
  color: rgba(255, 255, 255, 0.7);
  margin: 0;
}

.hero-metric-container {
  text-align: center;
  margin-bottom: 80px;
}

.hero-metric {
  @extend .serif-headline;
  font-size: 200px;
  line-height: 1;
  margin-bottom: 16px;

  &.score-high {
    background: linear-gradient(180deg, #fff 0%, $emerald 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  &.score-medium {
    background: linear-gradient(180deg, #fff 0%, $amber 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  &.score-low {
    background: linear-gradient(180deg, #fff 0%, $rose 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
}

.hero-label {
  @extend .mono-label;
  font-size: 18px;
  color: rgba(255, 255, 255, 0.6);
}

.stats-row {
  display: flex;
  gap: 24px;
  justify-content: center;
}

.stat-box {
  @extend .glass-card;
  min-width: 180px;
  padding: 24px 32px;
  text-align: center;
}

.stat-value {
  font-family: $font-body;
  font-size: 36px;
  font-weight: 700;
  color: #fff;
  margin-bottom: 8px;

  &.is-change-positive {
    color: $emerald;
  }

  &.is-change-negative {
    color: $rose;
  }

  &.is-change-neutral {
    color: rgba(255, 255, 255, 0.7);
  }
}

.stat-label {
  @extend .mono-label;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
}

.slide-footer {
  position: absolute;
  bottom: 40px;
  left: 80px;

  .company-name {
    font-family: $font-body;
    font-size: 14px;
    color: rgba(255, 255, 255, 0.4);
  }
}
</style>
